import { useRoom } from '#~/room/hooks.js';
import type { HandlerProps } from '../types.js';
import { ActionTray } from './action-tray.jsx';
import { calcAlignmentPt, calcToleranceBbox } from './utils.js';
import { useWiring } from './wiring.js';
import { VideoAnnotation, type Annotation } from '@studio/design-system/video-annotation';
import { DetectionMode } from '@tryiris-ai/common.protojs/autotracking/v2/state';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useEffect, useMemo, type ReactElement } from 'react';

/** `Tool.Zoom` `<Handler>` component. */
export const Handler = ({ deviceId, focused, ...props }: HandlerProps): ReactElement => {
  const agentId = useRoom((µ) => µ.devices[deviceId]?.agentId)!;

  const wiring = useWiring({ agentId, deviceId });

  const {
    deadband,
    deadbandSafetyArea,
    detectionMode,
    handleChangeToleranceDeadband,
    handleSelectAnnotation,
    handleDrawBoundingBox,
    selectedSubjectId,
    hasAnnotations,

    handleCreateReservation,
    handleDestroyReservation
  } = wiring;

  const canShowDeadband =
    selectedSubjectId !== undefined &&
    deadbandSafetyArea &&
    hasAnnotations &&
    (detectionMode === DetectionMode.DETECTION_MODE_TRACKING ||
      detectionMode === DetectionMode.DETECTION_MODE_REDETECTING ||
      detectionMode === DetectionMode.DETECTION_MODE_SEARCHING);

  const toleranceBBox = calcToleranceBbox(deadband);
  const alignmentPt = calcAlignmentPt(deadband);

  const alignmentBBox = {
    x: alignmentPt?.x ?? 0,
    y: alignmentPt?.y ?? 0
  };

  useEffect(() => {
    let mounted = true;
    let created = false;

    const fn = async () => {
      while (mounted) {
        const ok = await handleCreateReservation();

        if (ok) {
          created = true;
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    };

    const timeout = setTimeout(fn, 500);

    return () => {
      clearTimeout(timeout);

      mounted = false;

      if (!created) {
        return;
      }

      handleDestroyReservation(false);
    };
  }, []);

  /*
  useEffect(() => {
    if(!hadReservation && hasReservation) {
      setHadReservation(true);
    
      return () => {};
    }

    if(hadReservation && !hasReservation && reservationCreated) {
      let mounted = true;
      let created = false;

      const fn = async () => {
        while(mounted) {
          const ok = await handleCreateReservation();

          if (ok) {
            created = true;
            setReservationCreated(true);
            break
          }

          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }

      const timeout = setTimeout(fn, 500);

      return () => {
        clearTimeout(timeout);
        mounted = false;
      }

    }

    return () => {};
  }, [hasReservation, hadReservation, reservationCreated, setHadReservation, setReservationCreated]);
  */

  const annotationsMemo: T_Maybe<ReactElement> = useMemo(
    () => (
      <VideoAnnotation
        key={`autotracking_${deviceId}_${selectedSubjectId}_video_annotation`}
        alignmentDeadband={alignmentBBox}
        deadband={toleranceBBox}
        deadbandEditable
        deviceId={deviceId}
        deadbandSafetyArea={canShowDeadband}
        detectionMode={detectionMode}
        onChangeToleranceDeadband={(_deadband): void => {
          handleChangeToleranceDeadband(_deadband);
        }}
        onSelectAnnotation={(id: T_Maybe<Uint8Array>, annotation: Annotation): void => {
          const x1 = annotation.x;
          const y1 = annotation.y;
          const x2 = annotation.x + annotation.width;
          const y2 = annotation.y + annotation.height;

          const bbox = [x1, y1, x2, y2];

          handleSelectAnnotation(id, bbox);
        }}
        onDrawBoundingBox={(bbox): void => {
          handleDrawBoundingBox(bbox);
        }}
        selectedAnnotationId={selectedSubjectId}
      />
    ),
    [alignmentBBox, canShowDeadband, detectionMode, deviceId, selectedSubjectId, toleranceBBox]
  );

  return (
    <div key={`autotracking_${deviceId}_container`} {...props}>
      {focused && <ActionTray key={`autotracking_${deviceId}_action_tray`} {...wiring} />}

      <div className='absolute top-0 z-20 h-full w-full'>
        {focused || detectionMode === DetectionMode.DETECTION_MODE_TRACKING
          ? annotationsMemo
          : null}
      </div>
    </div>
  );
};
